import type { DepartmentAddress, PostalAddress } from "~~/models/addresses";

// formatting for address, currency, date without a dependency on i18n (can be used in triggers)

function formatAddress(addressLine1: string, addressLine2: string, postalCode: string, city: string, separator: "comma" | "newLine"): string {
  const separatorValue = separator === "comma" ? ", " : "<br>";
  return `${addressLine1}${addressLine2}${separatorValue}${postalCode} ${city}`;
}

/**
 * @description Format the postal address
 * @param address the address to format
 * @param separator "comma" | "newLine"; "newLine" is used for HTML and uses <br> as separator
 * @returns formatted address
 */
export function formatPostalAddress(address: PostalAddress | undefined | null, separator: "comma" | "newLine"): string {
  if (!address) {
    return "";
  }

  const addressLine2 = address.addressLine2?.length ? ` ${address.addressLine2}` : "";
  return formatAddress(address.addressLine1, addressLine2, address.postalCode, address.city, separator);
}

/**
 * @description Format the department address
 * @param address the address to format
 * @param separator "comma" | "newLine"; "newLine" is used for HTML and uses <br> as separator
 * @returns formatted address
 */
export function formatDepartmentAddress(address: DepartmentAddress | undefined | null, separator: "comma" | "newLine"): string {
  if (!address) {
    return "";
  }

  const addressLine2 = address.addressLine2?.length ? ` ${address.addressLine2}` : "";
  return formatAddress(address.addressLine1, addressLine2, address.postalCode, address.city, separator);
}

export function formatCurrencyLocale(locale: string, value: number | undefined | null): string {
  if (value === undefined || value === null) {
    return "?";
  }

  const { format } = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
  });

  return format(value);
}

export function formatDateForCheckout(value?: Date): string {
  if (!value) {
    return "";
  }

  return JSON.stringify(value).replace(/"([^"]+)"/g, "$1");
}

export function formatDateForSearch(value: Date): string {
  return `${value.getFullYear()}-${value.getMonth() + 1}-${value.getDate()}`;
}

export function formatDateLocale(value: Date, intlLocale: string, options?: Intl.DateTimeFormatOptions): string {
  try {
    const format = new Intl.DateTimeFormat(intlLocale, options).format;

    return format(value);
  }
  catch (e) {
    console.error("Could not format date", { value: value }, e);
    return value?.toString();
  }
}
